import { useState } from "react";
import { Star } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import {
  useCompany,
  useCompanyArticles,
  useCompanyEarnings,
  usePrices,
  useSetStar,
} from "@/hooks/api";
import { useIsMobile } from "@/hooks/useIsMobile";
import { cn } from "@/lib/utils";
import { formatCompactUsd, formatInt, formatPct, formatPrice } from "@/lib/format";
import { ArticlesTable } from "@/components/views/companies/ArticlesTable";
import { StockChart, type ChartRange } from "@/components/views/companies/StockChart";

const RANGES: ChartRange[] = ["1W", "1M", "3M", "1Y", "5Y", "ALL"];

const RANGE_FETCH_DAYS: Record<ChartRange, number | null> = {
  "1W": 365,
  "1M": 365,
  "3M": 365,
  "1Y": 365,
  "5Y": 365 * 5,
  ALL: null,
};

function startDateForRange(range: ChartRange): string | undefined {
  const days = RANGE_FETCH_DAYS[range];
  if (days === null) return "1970-01-01";
  if (days <= 365) return undefined;
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - days);
  return d.toISOString().slice(0, 10);
}

export function CompanyMain({ symbol }: { symbol: string | null }) {
  const [range, setRange] = useState<ChartRange>("1M");
  const [locked, setLocked] = useState<string | null>(null);
  const isMobile = useIsMobile();
  const { data: company } = useCompany(symbol);
  const setStar = useSetStar();
  const { data: prices } = usePrices(symbol, startDateForRange(range));
  const { data: earnings } = useCompanyEarnings(symbol);
  const { data: articles, isLoading: articlesLoading } = useCompanyArticles(symbol, locked);

  if (!symbol) {
    return (
      <div className="p-8 text-muted-foreground text-sm">
        Pick a company from the sidebar to see its chart and articles.
      </div>
    );
  }
  if (!company) {
    return <div className="p-8 text-muted-foreground text-sm">Loading…</div>;
  }

  const up = (company.change_pct ?? 0) >= 0;
  const upColor = "text-[hsl(var(--chart-up))]";
  const downColor = "text-[hsl(var(--chart-down))]";

  return (
    <div className="h-full overflow-y-auto md:flex md:flex-col md:overflow-hidden">
      <div className="px-3 md:px-4 py-3 border-b border-border flex flex-col gap-2 md:flex-row md:items-start md:justify-between md:gap-4">
        <div className="min-w-0">
          <div className="flex items-baseline gap-2 flex-wrap">
            <button
              type="button"
              onClick={() =>
                company.primary_symbol &&
                setStar.mutate({
                  symbol: company.primary_symbol,
                  starred: !company.starred,
                })
              }
              disabled={!company.primary_symbol || setStar.isPending}
              aria-pressed={company.starred}
              title={company.starred ? "Unstar company" : "Star company"}
              className="self-center -ml-0.5 disabled:opacity-50"
            >
              <Star
                className={cn(
                  "h-4 w-4 transition-colors",
                  company.starred
                    ? "text-amber-400 fill-amber-400"
                    : "text-muted-foreground hover:text-amber-400",
                )}
              />
            </button>
            <div className="font-mono font-bold text-[18px] whitespace-nowrap">
              {company.primary_symbol ?? "—"}
            </div>
            <div className="text-[12px] text-muted-foreground truncate">{company.name}</div>
            {company.sector && <Badge variant="outline">{company.sector}</Badge>}
          </div>
          <div className="flex items-baseline gap-3 mt-1 flex-wrap">
            <div className="font-mono font-bold text-[22px] tabular-nums whitespace-nowrap">
              {formatPrice(company.last_price)}
            </div>
            <div
              className={cn(
                "font-mono text-[12px] tabular-nums whitespace-nowrap",
                company.change_pct === null
                  ? "text-muted-foreground"
                  : up
                    ? upColor
                    : downColor,
              )}
            >
              {company.change !== null && company.change !== undefined ? (
                <>
                  {up ? "▲" : "▼"} {Math.abs(Number(company.change)).toFixed(2)} (
                  {formatPct(company.change_pct)})
                </>
              ) : (
                "—"
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center justify-between gap-2 md:flex-col md:items-end md:gap-2 shrink-0">
          <div className="overflow-x-auto -mx-1 px-1 md:overflow-visible md:mx-0 md:px-0">
            <ToggleGroup
              type="single"
              value={range}
              onValueChange={(v) => v && setRange(v as ChartRange)}
              className="rounded-[4px] border border-border bg-muted/30 p-0.5 gap-0.5"
            >
              {RANGES.map((r) => (
                <ToggleGroupItem
                  key={r}
                  value={r}
                  className="h-6 px-2.5 md:h-5 md:px-2 text-[11px] font-medium"
                >
                  {r}
                </ToggleGroupItem>
              ))}
            </ToggleGroup>
          </div>
          <div className="text-[10px] text-muted-foreground font-mono whitespace-nowrap">
            Next earnings:{" "}
            <span className="text-foreground">{company.next_earnings_date ?? "—"}</span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 border-b border-border text-[10px] font-mono divide-x divide-y md:divide-y-0 divide-border">
        <Stat label="MARKET CAP" value={formatCompactUsd(company.market_cap_usd)} />
        <Stat label="REVENUE TTM" value={formatCompactUsd(company.revenue_ttm_usd)} />
        <Stat
          label="PRED 2D"
          value={formatPct(company.predictions.d2 === null ? null : company.predictions.d2 * 100)}
          pos={company.predictions.d2 === null ? undefined : company.predictions.d2 > 0}
        />
        <Stat
          label="PRED 1W"
          value={formatPct(
            company.predictions.week === null ? null : company.predictions.week * 100,
          )}
          pos={company.predictions.week === null ? undefined : company.predictions.week > 0}
        />
        <Stat
          label="PRED 1M"
          value={formatPct(
            company.predictions.month === null ? null : company.predictions.month * 100,
          )}
          pos={company.predictions.month === null ? undefined : company.predictions.month > 0}
        />
        <Stat label="ARTICLES" value={formatInt(company.article_count)} />
      </div>

      <div className="px-3 md:px-4 py-3 border-b border-border">
        <StockChart
          series={prices}
          range={range}
          lockedDate={locked}
          onLockedChange={setLocked}
          earnings={earnings}
          height={isMobile ? 240 : 360}
        />
      </div>

      <div className="md:flex-1 md:overflow-hidden flex flex-col">
        <ArticlesTable
          articles={articles ?? []}
          dateLabel={locked}
          loading={!!locked && articlesLoading}
        />
      </div>
    </div>
  );
}

function Stat({ label, value, pos }: { label: string; value: string; pos?: boolean }) {
  return (
    <div className="px-3 py-2">
      <div className="text-muted-foreground uppercase tracking-wider text-[9px] whitespace-nowrap">
        {label}
      </div>
      <div
        className={cn(
          "text-[13px] font-semibold tabular-nums mt-0.5 whitespace-nowrap",
          pos === true && "text-[hsl(var(--chart-up))]",
          pos === false && "text-[hsl(var(--chart-down))]",
        )}
      >
        {value}
      </div>
    </div>
  );
}
